import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { RelationshipType } from '@/types/announcement';

export interface RecommendedRecipient {
  recipientName: string;
  relationship: RelationshipType | null;
  totalSent: number;
  lastSentAt: string | null;
}

/**
 * Fetches the user's past recipients sorted by share frequency,
 * optionally filtered by relationship type.
 */
export function useRecommendedRecipients(relationshipFilter?: RelationshipType | null) {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState<RecommendedRecipient[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setRecipients([]);
      return;
    }

    let cancelled = false;
    const fetch = async () => {
      setIsLoading(true);

      let query = supabase
        .from('recipient_contact_state')
        .select('recipient_name, relationship, total_sent_count, last_sent_at')
        .eq('user_id', user.id)
        .order('total_sent_count', { ascending: false })
        .limit(10);

      if (relationshipFilter) {
        query = query.eq('relationship', relationshipFilter);
      }

      const { data, error } = await query;

      if (!cancelled && !error && data) {
        setRecipients(
          data.map((r) => ({
            recipientName: r.recipient_name,
            relationship: (r.relationship as RelationshipType) || null,
            totalSent: r.total_sent_count ?? 0,
            lastSentAt: r.last_sent_at,
          }))
        );
      }
      if (!cancelled) setIsLoading(false);
    };

    fetch();
    return () => { cancelled = true; };
  }, [user, relationshipFilter]);

  return { recipients, isLoading };
}
